import { useState } from 'react'
import { Plus, Edit2, ToggleLeft, ToggleRight, Building } from 'lucide-react'

function CompanyModal({ company, branchId, branchOptions = [], onSave, onClose }) {
  const [companyName, setCompanyName] = useState(company?.companyName || '')
  const [contactPhone, setContactPhone] = useState(company?.contactPhone || '')
  const [contactEmail, setContactEmail] = useState(company?.contactEmail || '')
  const [targetBranch, setTargetBranch] = useState(branchId || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Companies belong to a station. An admin viewing "all" would otherwise have
  // one silently picked for them, so let them say which.
  const showBranchPicker = !company && branchOptions.length > 1

  async function handleSubmit(e) {
    e.preventDefault()
    if (!companyName.trim()) { setError('Company name is required.'); return }
    if (!targetBranch) { setError('Choose which station this company belongs to.'); return }
    setSaving(true)
    const result = await onSave({ companyName: companyName.trim(), contactPhone, contactEmail, branchId: targetBranch })
    setSaving(false)
    if (!result.success) { setError(result.message); return }
    onClose()
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50" onClick={onClose} />
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[70] bg-white rounded-2xl shadow-2xl p-5 max-w-sm mx-auto max-h-[calc(100dvh-6rem)] overflow-y-auto">
        <h3 className="font-bold text-gray-800 mb-4">{company ? 'Edit Company' : 'Add Transport Company'}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          {showBranchPicker && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Station *</label>
              <select
                value={targetBranch}
                onChange={(e) => setTargetBranch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              >
                {branchOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
            <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. ABC Logistics Ltd"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
            <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
              placeholder="e.g. 08012345678"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
            <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
              placeholder="e.g. fleet@abclogistics.com"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex space-x-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-60">
              {saving ? 'Saving…' : company ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default function TransportCompaniesPanel({ currentUser, branches, companies, onAdd, onUpdate, onToggle }) {
  // admin/superadmin: add + edit + (de)activate across the org
  // manager:          add within own branch (no edit/toggle)
  // staff:            view only
  const canModify = currentUser.role === 'admin' || currentUser.role === 'superadmin'
  const canAdd = canModify || currentUser.role === 'manager'
  const isBranchScoped = currentUser.role === 'manager' || currentUser.role === 'staff'

  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [search, setSearch] = useState('')
  const [branchFilter, setBranchFilter] = useState(currentUser.branchId || 'all')

  const visibleBranches = isBranchScoped
    ? branches.filter((b) => b.id === currentUser.branchId)
    : branches

  const addBranchId = branchFilter !== 'all' ? branchFilter : (visibleBranches[0]?.id || '')

  const filtered = companies.filter((c) => {
    const matchBranch = branchFilter === 'all' || c.branchId === branchFilter
    const matchSearch = !search || c.companyName.toLowerCase().includes(search.toLowerCase())
    return matchBranch && matchSearch
  })

  async function handleSave(data) {
    if (editing) return onUpdate(editing.id, data)
    return onAdd(data)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-800">Transport Companies</h2>
          <p className="text-xs text-gray-400">{filtered.length} company{filtered.length !== 1 ? 'ies' : ''}</p>
        </div>
        {canAdd && (
          <button
            onClick={() => { setEditing(null); setShowModal(true) }}
            disabled={!addBranchId}
            className="flex items-center space-x-1.5 bg-blue-600 text-white text-sm font-semibold px-3 py-2 rounded-xl disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /><span>Add</span>
          </button>
        )}
      </div>

      <div className="flex space-x-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
        />
        {!isBranchScoped && (
          <select
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="all">All branches</option>
            {visibleBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <Building className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No companies found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => {
            const branch = branches.find((b) => b.id === c.branchId)
            return (
              <div key={c.id} className={`bg-white rounded-xl border px-4 py-3 ${c.active ? 'border-gray-100' : 'border-gray-100 opacity-60'}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-gray-800">{c.companyName}</p>
                    <p className="text-xs text-gray-400">{c.contactPhone || 'No phone'} · {branch?.name}</p>
                  </div>
                  {canModify && (
                    <div className="flex items-center space-x-2 ml-3 flex-shrink-0">
                      <button
                        onClick={() => { setEditing(c); setShowModal(true) }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onToggle(c.id, !c.active)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition"
                      >
                        {c.active
                          ? <ToggleRight className="w-5 h-5 text-green-500" />
                          : <ToggleLeft className="w-5 h-5 text-gray-300" />
                        }
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {showModal && (
        <CompanyModal
          company={editing}
          branchId={editing ? editing.branchId : addBranchId}
          branchOptions={visibleBranches}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditing(null) }}
        />
      )}
    </div>
  )
}
